import React from 'react';
import { RichTextParser, type EntityListRow as EntityListRowModel } from 'coop-adventure-engine';
import { RichTextRenderer } from './RichTextRenderer';

export interface EntityListProps {
  rows: EntityListRowModel[];
  onRowVisit?: (row: EntityListRowModel) => void;
  /**
   * Present when `<Entity-List>` embed uses reference selection; disables visit
   * and drives {@link onEntityReferencePick} instead.
   */
  referenceSelect?: {
    targetScene: string;
    variableName: string;
  };
  /** Fired with entity UUID when reference mode is active. */
  onEntityReferencePick?: (entityId: string) => void;
  /** Fired for Entity-List `reorder=true` up/down controls. */
  onRowReorder?: (row: EntityListRowModel, direction: 'up' | 'down') => void;
  /** When true, rows do not navigate (e.g. global action pending). */
  disableInteractions?: boolean;
  customColor?: string | undefined;
  className?: string;
  /** Optional empty-state message when there are no rows. */
  emptyMessage?: string;
}

function ReorderChevron({ direction }: { direction: 'up' | 'down' }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      width="14"
      height="14"
      focusable="false"
    >
      {direction === 'up' ? (
        <path fill="currentColor" d="M10 5.5 16 13H4l6-7.5z" />
      ) : (
        <path fill="currentColor" d="M10 14.5 4 7h12l-6 7.5z" />
      )}
    </svg>
  );
}

/**
 * Renders engine-computed entity rows for an `<Entity-List>` embed.
 * When a row has `content` (from `contentVariable`), that rich text is the card body;
 * otherwise the plain `displayName` title is shown.
 */
const EntityList: React.FC<EntityListProps> = ({
  rows,
  onRowVisit,
  referenceSelect,
  onEntityReferencePick,
  onRowReorder,
  disableInteractions = false,
  customColor,
  className = '',
  emptyMessage = 'No entities here yet.'
}) => {
  if (!rows.length) {
    return (
      <p
        className={`entity-list-empty my-3 text-sm opacity-80 ${className}`.trim()}
        data-testid="entity-list-empty"
        role="status"
      >
        {emptyMessage}
      </p>
    );
  }

  const refMode = Boolean(referenceSelect && onEntityReferencePick);

  return (
    <ul
      className={`entity-list activity-list my-4 space-y-3 list-none p-0 ${className}`.trim()}
      data-testid="entity-list"
      aria-label="Entities"
    >
      {rows.map((row) => {
        const canInteract =
          !disableInteractions &&
          (refMode ? Boolean(onEntityReferencePick) : Boolean(onRowVisit));
        const isActiveCard = canInteract;
        const activeStyle: React.CSSProperties | undefined =
          isActiveCard && customColor
            ? {
                border: `1px solid ${customColor}40`,
                background: `linear-gradient(135deg, ${customColor}88, ${customColor}AA)`
              }
            : undefined;

        const customContent =
          typeof row.content === 'string' ? row.content.trim() : '';
        const customRichText = customContent
          ? RichTextParser.parseRichTextDescription(customContent)
          : undefined;
        const useCustomContent = Boolean(
          customRichText && customRichText.elements.length > 0
        );
        const trailing =
          typeof row.trailing === 'string' ? row.trailing.trim() : '';
        const showReorder =
          typeof row.canMoveUp === 'boolean' || typeof row.canMoveDown === 'boolean';
        const cardClass = `entity-card activity-card ${isActiveCard ? 'is-active' : 'is-inactive'}${
          useCustomContent ? ' entity-card--custom-content' : ''
        }${trailing ? ' entity-card--with-trailing' : ''}${
          showReorder ? ' entity-card--with-reorder' : ''
        }`;

        const inner = useCustomContent ? (
          <div
            className={`entity-card-custom-content activity-card-header text-left${
              trailing ? ' entity-card-header--with-trailing' : ''
            }`}
            data-testid={`entity-row-content-${row.entityId}`}
          >
            <div className="entity-card-custom-content-body">
              <RichTextRenderer
                description={customRichText!}
                className="rich-text entity-list-custom-content"
                {...(customColor ? { customColor } : {})}
              />
            </div>
            {trailing ? (
              <span
                className="entity-card-trailing activity-card-title-trailing"
                data-testid={`entity-row-trailing-${row.entityId}`}
              >
                {trailing}
              </span>
            ) : null}
          </div>
        ) : (
          <div className={`activity-card-header${trailing ? ' entity-card-header--with-trailing' : ''}`}>
            <span className="activity-card-title">
              <span className="activity-card-title-leading">{row.displayName}</span>
              {trailing ? (
                <span
                  className="entity-card-trailing activity-card-title-trailing"
                  data-testid={`entity-row-trailing-${row.entityId}`}
                >
                  {trailing}
                </span>
              ) : null}
            </span>
          </div>
        );

        const visitLabel = refMode
          ? `Select entity: ${row.displayName}`
          : `Visit entity: ${row.displayName}`;

        const visitBody = canInteract ? (
          <button
            type="button"
            className={showReorder ? 'entity-card-main' : cardClass}
            style={showReorder ? undefined : activeStyle}
            onClick={() =>
              refMode
                ? onEntityReferencePick?.(row.entityId)
                : onRowVisit?.(row)
            }
            data-testid={`entity-row-${row.entityId}`}
            aria-label={visitLabel}
          >
            {inner}
          </button>
        ) : (
          <div
            className={showReorder ? 'entity-card-main' : cardClass}
            style={showReorder ? undefined : activeStyle}
            data-testid={`entity-row-${row.entityId}`}
            aria-disabled="true"
            role="group"
            aria-label={`${row.displayName}, unavailable`}
          >
            {inner}
          </div>
        );

        const reorder = showReorder ? (
          <div className="entity-card-reorder" data-testid={`entity-row-reorder-${row.entityId}`}>
            <button
              type="button"
              className="entity-card-reorder-button"
              aria-label={`Move ${row.displayName} up`}
              data-testid={`entity-row-move-up-${row.entityId}`}
              disabled={disableInteractions || !onRowReorder || row.canMoveUp !== true}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (disableInteractions || row.canMoveUp !== true) {
                  return;
                }
                onRowReorder?.(row, 'up');
              }}
            >
              <ReorderChevron direction="up" />
            </button>
            <button
              type="button"
              className="entity-card-reorder-button"
              aria-label={`Move ${row.displayName} down`}
              data-testid={`entity-row-move-down-${row.entityId}`}
              disabled={disableInteractions || !onRowReorder || row.canMoveDown !== true}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (disableInteractions || row.canMoveDown !== true) {
                  return;
                }
                onRowReorder?.(row, 'down');
              }}
            >
              <ReorderChevron direction="down" />
            </button>
          </div>
        ) : null;

        return (
          <li key={row.entityId}>
            {showReorder ? (
              <div className={cardClass} style={activeStyle} data-testid={`entity-card-${row.entityId}`}>
                {visitBody}
                {reorder}
              </div>
            ) : (
              visitBody
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default EntityList;
